import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon,
  SparklesIcon,
  PuzzlePieceIcon,
  ClipboardDocumentListIcon,
  ArrowRightIcon,
  FlagIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  link: string;
  gradient: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon: Icon, title, description, link, gradient }) => {
  return (
    <Link 
      to={link}
      className="block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-all duration-200 group"
    >
      <div className={`inline-flex p-3 rounded-xl ${gradient} mb-4`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
        {title}
      </h3>
      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-3">
        {description}
      </p>
      <div className="flex items-center text-primary-600 dark:text-primary-400 text-sm font-medium">
        Saiba mais
        <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
};

const Index: React.FC = () => {
  const pageUrl = 'https://upmindy.com/docs';
  const pageTitle = 'Documentação | Upmindy - Guia Completo de Recursos';
  const pageDescription = 'Aprenda a usar todos os recursos do Upmindy: Flashcards com repetição espaçada, Quizzes interativos, Jogos de Combinação e Controle de Estudos.';

  const features: FeatureCardProps[] = [
    {
      icon: AcademicCapIcon,
      title: 'Flashcards',
      description: 'Cartões de estudo com repetição espaçada inteligente. Memorize vocabulário, conceitos e fatos de forma eficiente com nosso algoritmo cientificamente comprovado.',
      link: '/docs/flashcards',
      gradient: 'bg-gradient-to-br from-blue-500 to-blue-600'
    },
    {
      icon: SparklesIcon,
      title: 'Quizzes',
      description: 'Testes de múltipla escolha com sistema de revisão adaptativa. Avalie seu conhecimento e reforce seu aprendizado com feedback imediato e estatísticas detalhadas.',
      link: '/docs/quizzes',
      gradient: 'bg-gradient-to-br from-purple-500 to-purple-600'
    },
    {
      icon: PuzzlePieceIcon,
      title: 'Match Game',
      description: 'Jogo de memória educativo para parear conceitos relacionados. Uma forma divertida e eficaz de reforçar seu aprendizado através do jogo.',
      link: '/feature/match-game',
      gradient: 'bg-gradient-to-br from-pink-500 to-pink-600'
    },
    {
      icon: ClipboardDocumentListIcon,
      title: 'Módulo de Estudos',
      description: 'Sistema completo de gerenciamento de estudos. Organize recursos, acompanhe progresso, defina prioridades e mantenha seus estudos estruturados.',
      link: '/docs/studies',
      gradient: 'bg-gradient-to-br from-green-500 to-green-600'
    }
  ];

  return (
    <div className="prose dark:prose-invert max-w-none">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="Upmindy" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Documentação do Upmindy
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
          Aprenda a usar todos os recursos da plataforma para otimizar seus estudos e alcançar seus objetivos de aprendizagem.
        </p>
      </div>

      <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-2xl p-6 border border-primary-200 dark:border-primary-800 mb-8 not-prose">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center">
          <FlagIcon className="h-6 w-6 mr-2 text-primary-600 dark:text-primary-400" />
          Comece por aqui
        </h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          O Upmindy oferece múltiplas formas de aprender, cada uma projetada para diferentes necessidades. 
          Explore os recursos abaixo e descubra qual funciona melhor para você. Você pode combinar todos eles para maximizar seu aprendizado!
        </p>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Recursos Principais</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 not-prose">
        {features.map((feature, index) => (
          <FeatureCard key={index} {...feature} />
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Como usar esta documentação
        </h2>
        <div className="space-y-3 text-gray-600 dark:text-gray-300">
          <p>
            <strong className="text-gray-900 dark:text-white">Por recurso:</strong> Clique em qualquer recurso acima para aprender sobre suas funcionalidades específicas, casos de uso e melhores práticas.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Guias práticos:</strong> Cada página contém exemplos práticos e instruções passo a passo para você começar rapidamente.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Dicas e truques:</strong> Descubra funcionalidades avançadas e otimize seu fluxo de estudo.
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800 not-prose">
        <div className="flex items-start">
          <LightBulbIcon className="h-8 w-8 mr-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Dica: Combine os recursos
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Para obter os melhores resultados, use diferentes recursos para diferentes necessidades: 
              Flashcards para memorização, Quizzes para teste de conhecimento, Match para prática ludificada, 
              e Controle de Estudos para organizar todo o seu material de aprendizado.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
